import React from "react";
import { Search, Inbox, AlertTriangle, RefreshCw, X, ChevronUp, Bookmark, BookmarkPlus } from "lucide-react";
import { cn } from "@/lib/utils";

const TONES = {
  neutral: "bg-stone-100 text-stone-700",
  amber: "bg-amber-100 text-amber-800",
  emerald: "bg-emerald-100 text-emerald-800",
  blue: "bg-blue-100 text-blue-800",
  red: "bg-red-100 text-red-700",
  violet: "bg-violet-100 text-violet-800",
  dark: "bg-emerald-900 text-saffron-400",
};
const STATUS_TONE = {
  submitted: "neutral", pending: "amber", "verification pending": "amber", verification: "amber", pending_review: "amber",
  requested: "amber", extracting: "amber", approved: "blue", disbursed: "blue", "in review": "amber", verified: "emerald",
  paid: "emerald", transferred: "emerald", repaid: "emerald", active: "emerald", done: "emerald", issued: "emerald",
  rejected: "red", failed: "red", disabled: "red", cancelled: "red", due: "violet", outstanding: "violet", processing: "neutral",
};
export const toneFor = (s) => STATUS_TONE[String(s || "").toLowerCase()] || "neutral";

export function StatusBadge({ status, label, tone, className, testId, size = "sm" }) {
  const t = TONES[tone || toneFor(status)] || TONES.neutral;
  return (
    <span data-testid={testId} className={cn("inline-flex items-center rounded-full font-bold whitespace-nowrap", size === "xs" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs", t, className)}>
      {label ?? status}
    </span>
  );
}

export function Chip({ children, tone = "neutral", className, testId, mono }) {
  return <span data-testid={testId} className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold", TONES[tone] || TONES.neutral, mono && "font-mono", className)}>{children}</span>;
}

export function Code({ children, className, testId }) {
  return <span data-testid={testId} className={cn("font-mono text-[10px] font-bold text-emerald-800 tracking-wide", className)}>{children}</span>;
}

export function KV({ label, value, mono, testId, className, tone }) {
  return (
    <div className={cn("rounded-xl border border-stone-200 bg-stone-50/70 px-3 py-2.5 min-w-0", className)}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
      <div data-testid={testId} className={cn("mt-0.5 text-sm text-stone-900 truncate", mono ? "font-mono" : "font-semibold", tone)}>{value ?? <span className="text-stone-400">—</span>}</div>
    </div>
  );
}

export function CardGrid({ children, cols = 3, dense, className, testId }) {
  const c = { 1: "grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 xl:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" }[cols];
  return <div data-testid={testId} className={cn("grid stagger", dense ? "gap-2.5" : "gap-4", c, className)}>{children}</div>;
}

export function RecordCard({ title, code, sub, badge, meta = [], right, actions, children, onClick, selected, onSelect, selectTestId, avatar, expanded, dense, className, testId, tone }) {
  const clickable = !!onClick;
  return (
    <article
      data-testid={testId}
      onClick={expanded ? undefined : onClick}
      className={cn(
        "group relative flex flex-col rounded-2xl border border-stone-200 bg-white text-left min-w-0",
        dense ? "p-3.5 gap-2.5" : "p-5 gap-3",
        clickable && !expanded && "cursor-pointer",
        selected && "ring-2 ring-saffron-500 border-saffron-400",
        expanded && "col-span-full ring-1 ring-emerald-900/20",
        tone, className,
      )}>
      <div className={cn("flex items-start gap-3 min-w-0", expanded && clickable && "cursor-pointer")} onClick={expanded ? onClick : undefined} data-testid={testId ? `${testId}-head` : undefined}>
        {onSelect && (
          <input type="checkbox" data-testid={selectTestId} checked={!!selected} onClick={(e) => e.stopPropagation()} onChange={onSelect} className="mt-1.5 w-4 h-4 accent-emerald-800 shrink-0" />
        )}
        {avatar && <div className="w-10 h-10 rounded-xl bg-emerald-900 text-saffron-400 grid place-items-center font-display font-black text-sm shrink-0">{avatar}</div>}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            {code && <Code>{code}</Code>}
            <h3 className="font-display font-bold text-stone-900 text-base leading-tight truncate">{title}</h3>
            {badge}
          </div>
          {sub && <div className="text-sm text-stone-600 mt-0.5 truncate">{sub}</div>}
        </div>
        {right && <div className="text-right shrink-0">{right}</div>}
        {expanded && clickable && <ChevronUp className="w-4 h-4 text-stone-400 shrink-0 mt-1" />}
      </div>
      {meta.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500">
          {meta.filter(Boolean).map((m, i) => <span key={i} className="inline-flex items-center gap-1 min-w-0 truncate">{m}</span>)}
        </div>
      )}
      {actions && <div className="flex flex-wrap items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>{actions}</div>}
      {expanded && children && <div className="mt-2 border-t border-stone-200 pt-5 cursor-default bento-expand" onClick={(e) => e.stopPropagation()}>{children}</div>}
    </article>
  );
}

const PRESET_NS = "crece_presets_";
export function usePresets(key) {
  const [presets, setPresets] = React.useState(() => { try { return JSON.parse(localStorage.getItem(PRESET_NS + key) || "[]"); } catch { return []; } });
  const persist = (next) => { setPresets(next); try { localStorage.setItem(PRESET_NS + key, JSON.stringify(next)); } catch { /* private mode */ } };
  return {
    presets,
    save: (name, state) => persist([...presets.filter((p) => p.name !== name), { name, ...state, at: Date.now() }]),
    remove: (name) => persist(presets.filter((p) => p.name !== name)),
  };
}

function PresetBar({ presetKey, active, search, onChange, onSearch, testId }) {
  const { presets, save, remove } = usePresets(presetKey);
  const [naming, setNaming] = React.useState(false);
  const [name, setName] = React.useState("");
  const current = presets.find((p) => p.active === active && (p.search || "") === (search || ""));
  const commit = () => { const n = name.trim(); if (!n) return; save(n, { active, search: search || "" }); setName(""); setNaming(false); };
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testId ? `${testId}-presets` : undefined}>
      {presets.map((p) => (
        <span key={p.name} className={cn("inline-flex items-center rounded-full border text-[11px] font-bold chip-pop", current?.name === p.name ? "border-saffron-400 bg-saffron-50 text-emerald-950" : "border-stone-200 bg-white text-stone-600 hover:border-emerald-700")}>
          <button type="button" data-testid={testId ? `${testId}-preset-${p.name}` : undefined} onClick={() => { onChange?.(p.active); onSearch?.(p.search || ""); }} className="pl-2.5 pr-1 py-1 inline-flex items-center gap-1"><Bookmark className="w-3 h-3" />{p.name}</button>
          <button type="button" aria-label={`Remove preset ${p.name}`} onClick={() => remove(p.name)} className="pr-2 pl-0.5 py-1 text-stone-400 hover:text-red-600"><X className="w-3 h-3" /></button>
        </span>
      ))}
      {naming ? (
        <span className="inline-flex items-center gap-1">
          <input autoFocus data-testid={testId ? `${testId}-preset-name` : undefined} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setNaming(false); }} placeholder="Preset name" className="h-7 w-32 rounded-full border border-stone-200 px-2.5 text-xs" />
          <button type="button" data-testid={testId ? `${testId}-preset-commit` : undefined} onClick={commit} className="h-7 rounded-full bg-emerald-900 text-white text-[11px] font-bold px-2.5">Save</button>
        </span>
      ) : (
        !current && <button type="button" data-testid={testId ? `${testId}-preset-save` : undefined} onClick={() => setNaming(true)} title="Save this filter + search as a preset" className="inline-flex items-center gap-1 rounded-full border border-dashed border-stone-300 px-2.5 py-1 text-[11px] font-bold text-stone-500 hover:text-emerald-900 hover:border-emerald-700"><BookmarkPlus className="w-3 h-3" /> Save view</button>
      )}
    </div>
  );
}

export function FilterStrip({ chips = [], active, onChange, search, onSearch, searchPlaceholder = "Search…", right, children, className, testId, sticky = true, chipTestId, presetKey }) {
  const ref = React.useRef(null);
  const [stuck, setStuck] = React.useState(false);
  React.useEffect(() => {
    if (!sticky || !ref.current) return;
    const el = ref.current;
    const onScroll = () => setStuck(el.getBoundingClientRect().top <= 77);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sticky]);
  return (
    <div ref={ref} data-stuck={stuck} data-testid={testId} className={cn("glass rounded-2xl border border-stone-200/70 px-3 py-2 flex flex-wrap items-center gap-2 z-10", sticky && "sticky top-[76px]", className)}>
      {chips.length > 0 && (
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar max-w-full">
          {chips.map((c) => {
            const on = active === c.k;
            return (
              <button key={c.k} type="button" data-testid={chipTestId ? chipTestId(c.k) : testId ? `${testId}-chip-${c.k}` : undefined} onClick={() => onChange?.(c.k)}
                className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold transition-colors", on ? "bg-emerald-900 text-white" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900")}>
                {c.label}
                {c.count != null && <span className={cn("rounded-full px-1.5 py-px text-[10px] font-black font-mono", on ? "bg-saffron-500 text-emerald-950" : "bg-stone-200 text-stone-700")}>{c.count}</span>}
              </button>
            );
          })}
        </div>
      )}
      {onSearch && (
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input data-testid={testId ? `${testId}-search` : undefined} value={search || ""} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder}
            className="w-full h-9 rounded-full border border-stone-200 bg-white pl-9 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-saffron-400" />
          {search && <button type="button" onClick={() => onSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"><X className="w-3.5 h-3.5" /></button>}
        </div>
      )}
      {children}
      {presetKey && <PresetBar presetKey={presetKey} active={active} search={search} onChange={onChange} onSearch={onSearch} testId={testId} />}
      {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
    </div>
  );
}

const SPAN = { 1: "lg:col-span-1", 2: "lg:col-span-2", 3: "lg:col-span-3", 4: "lg:col-span-4", 5: "lg:col-span-5", 6: "lg:col-span-6" };
export function DetailBoard({ children, className, testId }) {
  return <div data-testid={testId} className={cn("grid gap-4 lg:grid-cols-6 stagger", className)}>{children}</div>;
}
export function Tile({ title, icon: Icon, span = 6, dark, tone, right, children, className, testId, bodyClass }) {
  return (
    <section data-testid={testId} className={cn("rounded-2xl border p-5 min-w-0", dark ? "bento-dark border-emerald-900" : cn("bg-white border-stone-200", tone), SPAN[span], className)}>
      {(title || right) && (
        <header className="flex items-center gap-2 mb-3">
          {Icon && <Icon className={cn("w-4 h-4", dark ? "text-saffron-400" : "text-emerald-800")} />}
          {title && <span className={cn("text-[11px] font-black uppercase tracking-[0.14em]", dark ? "text-white/70" : "text-stone-500")}>{title}</span>}
          {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function SectionForm({ title, desc, icon: Icon, children, cols = 2, className, testId, right }) {
  return (
    <section data-testid={testId} className={cn("rounded-2xl border border-stone-200 bg-white p-6", className)}>
      <header className="flex items-start gap-3">
        {Icon && <div className="w-10 h-10 rounded-xl bg-emerald-900 text-saffron-400 grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>}
        <div className="min-w-0">
          <h3 className="font-display text-lg font-black text-stone-900 leading-tight">{title}</h3>
          {desc && <p className="text-sm text-stone-500 mt-0.5">{desc}</p>}
        </div>
        {right && <div className="ml-auto">{right}</div>}
      </header>
      <div className={cn("mt-5 grid gap-4", { 1: "grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3" }[cols])}>{children}</div>
    </section>
  );
}

export function Field({ label, hint, error, children, className, full }) {
  return (
    <label className={cn("block min-w-0", full && "sm:col-span-full", className)}>
      <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">{label}</span>
      <div className="mt-1.5">{children}</div>
      {error ? <span className="block mt-1 text-xs font-bold text-red-700">{error}</span> : hint ? <span className="block mt-1 text-xs text-stone-400">{hint}</span> : null}
    </label>
  );
}

export function ActionBar({ children, className, testId }) {
  return (
    <div data-testid={testId} className={cn("sticky bottom-24 lg:bottom-4 z-30 rounded-2xl bento-dark border border-emerald-900 p-3 flex flex-wrap items-center gap-2 shadow-2xl", className)}>
      <div className="relative z-10 flex flex-wrap items-center gap-2 w-full">{children}</div>
    </div>
  );
}

export function EmptyTile({ icon: Icon = Inbox, title = "Nothing here yet", desc, action, className, testId }) {
  return (
    <div data-testid={testId} className={cn("col-span-full rounded-2xl border border-dashed border-stone-300 bg-white/60 p-10 text-left sm:text-center", className)}>
      <div className="w-12 h-12 rounded-2xl bg-stone-100 grid place-items-center sm:mx-auto"><Icon className="w-6 h-6 text-stone-400" /></div>
      <div className="font-display font-black text-lg text-stone-800 mt-3">{title}</div>
      {desc && <p className="text-sm text-stone-500 mt-1 max-w-md sm:mx-auto">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function SkeletonTiles({ n = 6, cols = 3, h = "h-28", testId }) {
  return (
    <CardGrid cols={cols} testId={testId}>
      {Array.from({ length: n }).map((_, i) => <div key={i} className={cn("rounded-2xl border border-stone-200 bg-white skeleton-shimmer", h)} />)}
    </CardGrid>
  );
}

export function ErrorTile({ message = "Something went wrong", onRetry, className, testId }) {
  return (
    <div data-testid={testId} className={cn("col-span-full rounded-2xl border border-red-200 bg-red-50 p-6 flex flex-wrap items-center gap-3", className)}>
      <AlertTriangle className="w-5 h-5 text-red-600" />
      <div className="font-bold text-red-800 text-sm flex-1">{message}</div>
      {onRetry && <button type="button" onClick={onRetry} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100"><RefreshCw className="w-3.5 h-3.5" /> Retry</button>}
    </div>
  );
}

export const btn = {
  primary: "inline-flex items-center gap-1.5 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold text-xs px-4 py-2 transition-colors disabled:opacity-50",
  accent: "inline-flex items-center gap-1.5 rounded-full bg-saffron-500 hover:bg-saffron-400 text-emerald-950 font-black text-xs px-4 py-2 transition-colors disabled:opacity-50",
  ghost: "inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white hover:bg-stone-50 text-stone-800 font-bold text-xs px-3.5 py-2 transition-colors disabled:opacity-50",
  danger: "inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white hover:bg-red-50 text-red-700 font-bold text-xs px-3.5 py-2 transition-colors disabled:opacity-50",
  icon: "p-2 rounded-full text-stone-600 hover:bg-emerald-50 hover:text-emerald-900 transition-colors",
};
